import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Pencil, Check, X } from "lucide-react";

import TrainingColumn from "./components/TrainingColumn";
import ChannelsColumn from "./components/ChannelsColumn";
import SimulatorColumn from "./components/SimulatorColumn";
import DeployColumn from "./components/DeployColumn";
import EcomWizard from "./components/EcomWizard";
import ParseLoadingModal from "./components/ParseLoadingModal";
import ExtractedPreviewDialog from "./components/ExtractedPreviewDialog";

export default function AgentBuilder() {
  const { agentId } = useParams();
  const nav = useNavigate();
  const [agent, setAgent] = useState(null);
  const [sources, setSources] = useState([]);
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ecom, setEcom] = useState(null);

  // KB add
  const [urlValue, setUrlValue] = useState("");
  const [crawlSite, setCrawlSite] = useState(false);
  const [textName, setTextName] = useState("");
  const [textBody, setTextBody] = useState("");

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState("");
  const [scanning, setScanning] = useState(false);

  // Inline rename
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [renamingBusy, setRenamingBusy] = useState(false);

  // Loading modal
  const [loading, setLoading] = useState({ open: false, title: "", subtitle: "", steps: [], variant: "training" });
  // Post-crawl preview ("here's what your agent learned")
  const [postCrawlPreview, setPostCrawlPreview] = useState(null); // { title, text, urls }
  const setLoadingSteps = (updater) => setLoading((l) => ({ ...l, steps: typeof updater === "function" ? updater(l.steps) : updater }));

  const loadEcom = async () => {
    try { const { data } = await api.get("/ecommerce/config", { params: { agent_id: agentId } }); setEcom(data); }
    catch { /* noop */ }
  };

  const load = async () => {
    const [a, s, opts] = await Promise.all([
      api.get(`/agents/${agentId}`),
      api.get(`/knowledge/sources`, { params: { agent_id: agentId } }),
      api.get(`/onboarding/options`),
    ]);
    setAgent(a.data); setSources(s.data); setVoices(opts.data.voices);
    setEcom(null); // reset between agents to prevent ghost ecom from previous agent
    loadEcom();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agentId]);

  if (!agent) return <div className="text-sm text-[#4B5563]">Loading…</div>;

  const channels = agent.channels?.length ? agent.channels : [agent.type || "web_chat"];

  const save = async () => {
    setSaving(true);
    try {
      const p = { ...agent }; delete p.id; delete p.tenant_id; delete p.created_at;
      await api.put(`/agents/${agentId}`, p);
      toast.success("Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const beginRename = () => {
    setNameDraft(agent.name || "");
    setRenaming(true);
  };

  const cancelRename = () => {
    setRenaming(false);
    setNameDraft("");
  };

  const commitRename = async () => {
    const next = nameDraft.trim();
    if (!next) { toast.error("Name can't be empty"); return; }
    if (next === agent.name) { cancelRename(); return; }
    if (next.length > 80) { toast.error("Name is too long (max 80)"); return; }
    setRenamingBusy(true);
    const prevName = agent.name;
    try {
      // Optimistically reflect immediately; rollback on failure.
      setAgent((a) => ({ ...a, name: next }));
      const p = { ...agent, name: next }; delete p.id; delete p.tenant_id; delete p.created_at;
      await api.put(`/agents/${agentId}`, p);
      toast.success("Renamed — your widget will show the new name on next load.");
      setRenaming(false);
    } catch (err) {
      setAgent((a) => ({ ...a, name: prevName }));
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally {
      setRenamingBusy(false);
    }
  };

  const addUrl = async () => {
    if (!urlValue.trim()) return;
    // Trigger wizard the FIRST time a URL is added (or whenever no ecom config exists)
    if (!ecom?.configured) {
      setPendingUrl(urlValue.trim());
      setWizardOpen(true);
      return;
    }
    await runUrlIngest(urlValue.trim());
  };

  const runUrlIngest = async (u) => {
    setLoading({
      open: true,
      title: "Training your agent",
      subtitle: u,
      variant: "training",
      steps: [
        { label: crawlSite ? "Discovering pages via sitemap" : "Loading page (headless render)", status: "active" },
        { label: "Extracting clean text", status: "pending" },
        { label: "Chunking & embedding", status: "pending" },
        { label: "Saving to your agent's brain", status: "pending" },
      ],
    });
    try {
      // Animate steps progressively while the request runs
      const stepTimers = [
        setTimeout(() => setLoadingSteps((s) => s.map((x, i) => i === 0 ? { ...x, status: "done" } : i === 1 ? { ...x, status: "active" } : x)), 1500),
        setTimeout(() => setLoadingSteps((s) => s.map((x, i) => i === 1 ? { ...x, status: "done" } : i === 2 ? { ...x, status: "active" } : x)), 5500),
        setTimeout(() => setLoadingSteps((s) => s.map((x, i) => i === 2 ? { ...x, status: "done" } : i === 3 ? { ...x, status: "active" } : x)), 9500),
      ];
      const { data } = await api.post("/knowledge/url", { agent_id: agentId, url: u, crawl_site: crawlSite, max_pages: 8 });
      stepTimers.forEach(clearTimeout);
      setLoadingSteps((s) => s.map((x) => ({ ...x, status: "done" })));
      setTimeout(() => setLoading({ open: false, title: "", subtitle: "", steps: [], variant: "training" }), 700);
      toast.success(data.pages ? `${data.pages} pages · ${data.chunks} chunks added` : `${data.chunks} chunks added`);
      setUrlValue("");
      const s = await api.get(`/knowledge/sources`, { params: { agent_id: agentId } });
      setSources(s.data);
      // Show the user what we actually learned — preview from the live response so they
      // can sanity-check the extraction without hunting for a button.
      if (data.preview) {
        setTimeout(() => setPostCrawlPreview({
          title: u,
          text: data.preview,
          urls: data.extracted_urls || [u],
          sourceId: data.source_id,
        }), 900);
      }
    } catch (err) {
      setLoadingSteps((s) => s.map((x) => x.status === "active" ? { ...x, status: "error" } : x));
      setTimeout(() => setLoading({ open: false, title: "", subtitle: "", steps: [], variant: "training" }), 2000);
      toast.error(err?.response?.data?.detail || "Failed to train from URL");
    }
  };

  const handleWizardSaved = async (cfg) => {
    setEcom(cfg);
    // Continue with URL ingestion that triggered the wizard
    if (pendingUrl) {
      await runUrlIngest(pendingUrl);
      setPendingUrl("");
    }
    // If they configured an ecom store, also auto-scan products in the background
    if (cfg?.site_type === "ecommerce") {
      runScan();
    }
  };

  const runScan = async () => {
    setScanning(true);
    setLoading({
      open: true,
      title: "Scanning your catalog",
      subtitle: ecom?.store_url || "",
      variant: "scanning",
      steps: [
        { label: "Fetching sitemap", status: "active" },
        { label: "Visiting product pages", status: "pending" },
        { label: "Extracting JSON-LD product data", status: "pending" },
        { label: "Saving to catalog cache", status: "pending" },
      ],
    });
    try {
      const stepTimers = [
        setTimeout(() => setLoadingSteps((s) => s.map((x, i) => i === 0 ? { ...x, status: "done" } : i === 1 ? { ...x, status: "active" } : x)), 1800),
        setTimeout(() => setLoadingSteps((s) => s.map((x, i) => i === 1 ? { ...x, status: "done" } : i === 2 ? { ...x, status: "active" } : x)), 6000),
        setTimeout(() => setLoadingSteps((s) => s.map((x, i) => i === 2 ? { ...x, status: "done" } : i === 3 ? { ...x, status: "active" } : x)), 10000),
      ];
      const { data } = await api.post("/ecommerce/scan", null, { params: { agent_id: agentId } });
      stepTimers.forEach(clearTimeout);
      setLoadingSteps((s) => s.map((x) => ({ ...x, status: "done" })));
      setTimeout(() => setLoading({ open: false, title: "", subtitle: "", steps: [], variant: "scanning" }), 700);
      toast.success(`Scanned ${data.count} products`);
      loadEcom();
    } catch (err) {
      setLoadingSteps((s) => s.map((x) => x.status === "active" ? { ...x, status: "error" } : x));
      setTimeout(() => setLoading({ open: false, title: "", subtitle: "", steps: [], variant: "scanning" }), 2000);
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const addText = async () => {
    if (!textName.trim() || !textBody.trim()) return;
    const t = toast.loading("Embedding…");
    try {
      await api.post("/knowledge/text", { agent_id: agentId, name: textName, text: textBody });
      toast.success("Knowledge added", { id: t });
      setTextName(""); setTextBody("");
      const s = await api.get(`/knowledge/sources`, { params: { agent_id: agentId } });
      setSources(s.data);
    } catch { toast.error("Failed", { id: t }); }
  };

  const uploadFile = async (fileRef) => {
    const f = fileRef.current?.files?.[0]; if (!f) return;
    const t = toast.loading(`Parsing ${f.name}…`);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("agent_id", agentId);
      await api.post("/knowledge/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File trained", { id: t });
      if (fileRef.current) fileRef.current.value = "";
      const s = await api.get(`/knowledge/sources`, { params: { agent_id: agentId } });
      setSources(s.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed", { id: t }); }
  };

  const removeSource = async (id) => {
    await api.delete(`/knowledge/source/${id}`);
    toast.success("Removed");
    const s = await api.get(`/knowledge/sources`, { params: { agent_id: agentId } });
    setSources(s.data);
  };

  return (
    <div data-testid="agent-builder-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} className="btn-outline text-xs px-2" data-testid="builder-back-button"><ArrowLeft className="w-4 h-4" /></button>
          <div className="min-w-0">
            {renaming ? (
              <div className="flex items-center gap-2" data-testid="agent-rename-form">
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename();
                    if (e.key === "Escape") cancelRename();
                  }}
                  maxLength={80}
                  disabled={renamingBusy}
                  data-testid="agent-rename-input"
                  className="font-display font-extrabold text-2xl bg-white border-b-2 border-[#F97316] focus:outline-none px-1 py-0.5 min-w-[260px] max-w-[480px] disabled:opacity-60"
                />
                <button
                  onClick={commitRename}
                  disabled={renamingBusy}
                  className="p-1.5 rounded-sm bg-[#F97316] text-white hover:bg-[#EA580C] disabled:opacity-60"
                  data-testid="agent-rename-save"
                  title="Save (Enter)"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={cancelRename}
                  disabled={renamingBusy}
                  className="p-1.5 rounded-sm border border-[#E2E8F0] text-[#4B5563] hover:bg-[#F1F5F9] disabled:opacity-60"
                  data-testid="agent-rename-cancel"
                  title="Cancel (Esc)"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="group flex items-center gap-2">
                <h1 className="font-display font-extrabold text-2xl truncate max-w-[420px]" data-testid="agent-name-heading">{agent.name}</h1>
                <button
                  onClick={beginRename}
                  className="p-1.5 rounded-sm text-[#4B5563] hover:text-[#F97316] hover:bg-[#FFF7ED] opacity-60 group-hover:opacity-100 transition"
                  data-testid="agent-rename-button"
                  title="Rename agent"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="text-xs font-mono text-[#4B5563]">{channels.join(" · ")} · {agent.voice_id} · {agent.language || "English"}{ecom?.platform ? ` · ${ecom.platform}` : ""}</div>
          </div>
        </div>
        <button onClick={save} disabled={saving || channels.length === 0} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="builder-save-button">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : channels.length === 0 ? "Enable a channel" : "Save changes"}
        </button>
      </div>

      <div className="grid xl:grid-cols-4 lg:grid-cols-2 gap-5">
        <TrainingColumn
          urlValue={urlValue} setUrlValue={setUrlValue}
          crawlSite={crawlSite} setCrawlSite={setCrawlSite}
          onAddUrl={addUrl}
          textName={textName} setTextName={setTextName}
          textBody={textBody} setTextBody={setTextBody}
          onAddText={addText}
          onUploadFile={uploadFile}
          sources={sources}
          onRemoveSource={removeSource}
        />
        <ChannelsColumn agent={agent} setAgent={setAgent} voices={voices} />
        <SimulatorColumn agentId={agentId} channels={channels} />
        <DeployColumn
          agentId={agentId}
          ecom={ecom}
          onScan={runScan}
          scanning={scanning}
          refreshEcom={() => { setPendingUrl(ecom?.store_url || urlValue || ""); setWizardOpen(true); }}
        />
      </div>

      <EcomWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialUrl={pendingUrl || urlValue || ecom?.store_url || ""}
        onSaved={handleWizardSaved}
        agentId={agentId}
      />

      <ParseLoadingModal {...loading} />

      <ExtractedPreviewDialog
        open={!!postCrawlPreview}
        onClose={() => setPostCrawlPreview(null)}
        sourceId={postCrawlPreview?.sourceId}
        title={postCrawlPreview?.title}
      />
    </div>
  );
}
